import { Stack, Switch, FormControlLabel, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import {
  useCreateCommunity,
  ApiResponse_Create_Community,
} from '@/api/endpoints/community/useCreateCommunity';

import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';

interface CreateCommunityRequest {
  name: string;
  description: string;
  require_verification: boolean;
}

interface CreateCommunityModalProps {
  onClose: () => void;
  onCommunityCreated?: (community: ApiResponse_Create_Community) => void;
}

const CreateCommunityModal: React.FC<CreateCommunityModalProps> = ({
  onClose,
  onCommunityCreated,
}) => {
  const form = useForm<CreateCommunityRequest>({
    defaultValues: {
      name: '',
      description: '',
      require_verification: true,
    },
    mode: 'onBlur',
  });

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const createCommunityMutation = useCreateCommunity({
    onSuccess: data => {
      onCommunityCreated?.(data);
      onClose();
    },
  });

  const onSubmit = useCallback(
    (data: CreateCommunityRequest) => createCommunityMutation.mutateAsync(data),
    [createCommunityMutation]
  );

  const requireVerification = watch('require_verification');

  return (
    <ModalWithForm
      form={form}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth="sm"
      title="Create a Community"
      buttonLabel="Create Community"
    >
      <Stack spacing={3}>
        <FormTextField
          control={control}
          label="Community Name"
          size="small"
          placeholder="Enter a descriptive name for your community"
          required
          error={!!errors.name}
          helperText={errors.name?.message}
          {...register('name', {
            required: 'Community name is required',
            minLength: {
              value: 3,
              message: 'Community name must be at least 3 characters long',
            },
            maxLength: {
              value: 100,
              message: 'Community name cannot exceed 100 characters',
            },
          })}
        />

        <FormTextField
          control={control}
          label="Description"
          size="small"
          multiline
          rows={3}
          placeholder="Describe what this community is about and what members can expect..."
          required
          error={!!errors.description}
          helperText={errors.description?.message}
          {...register('description', {
            required: 'Community description is required',
            minLength: {
              value: 10,
              message: 'Description must be at least 10 characters long',
            },
            maxLength: {
              value: 500,
              message: 'Description cannot exceed 500 characters',
            },
          })}
        />

        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={requireVerification}
                onChange={e => setValue('require_verification', e.target.checked)}
                color="primary"
              />
            }
            label="Require member verification"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
            {requireVerification
              ? 'New members will need to be approved before joining'
              : 'Anyone can join this community immediately'}
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> As the community creator, you will automatically become an admin
            with full management permissions.
          </Typography>
        </Stack>
      </Stack>
    </ModalWithForm>
  );
};

export default CreateCommunityModal;
